import { IApiConfig } from "./api";

export type CoreContentTemplateCategory = "Email" | "SMS";

export type CoreContentTemplateSearchRequest = {
  searchText?: string;
  category?: CoreContentTemplateCategory;
  pageIndex?: number;
  pageSize?: number;
};

export type CoreContentTemplateSummary = {
  id: string;
  templateId: string;
  name: string;
  category: string;
  format: string;
};

export type CoreContentTemplate = CoreContentTemplateSummary & {
  content: string;
  inputSchemaValue?: unknown;
};

export type CoreContentTemplateSearchResponse = {
  items: CoreContentTemplateSummary[];
  hasNextPage: boolean;
};

export class CoreContentTemplatesClient {
  private readonly http: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> };

  constructor(
    private readonly configuration: IApiConfig,
    private readonly baseUrl = "",
    http?: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> }
  ) {
    this.http = http ?? (window as any);
  }

  search(request: CoreContentTemplateSearchRequest = {}, signal?: AbortSignal): Promise<CoreContentTemplateSearchResponse> {
    return this.request("/CoreContentTemplates/Search", {
      method: "POST",
      body: JSON.stringify({
        ...request,
        pageIndex: request.pageIndex ?? 0,
        pageSize: request.pageSize ?? 20,
      }),
      signal,
    });
  }

  getById(templateId: string, signal?: AbortSignal): Promise<CoreContentTemplate> {
    return this.request(`/CoreContentTemplates/${encodeURIComponent(templateId)}`, { method: "GET", signal });
  }

  private async request<TResponse>(path: string, init: RequestInit): Promise<TResponse> {
    const token = await this.configuration.getAccessToken();
    const response = await this.http.fetch(`${this.baseUrl}${path}`, {
      ...init,
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });

    if (!response.ok) {
      const detail = await response.text();
      throw new Error(detail || `Core content template request failed with status ${response.status}.`);
    }

    return (await response.json()) as TResponse;
  }
}
